/**
 * Generate Jig stage-tools module.
 * Applies AI-authored stage tools to serialized Jig stage data, including labels,
 * transforms, material changes, alpha fades, and viewpoint updates.
 */
import type { StageInstructionWithTools, ToolData, ViewpointSceneContext, ViewpointToolData } from './generate-jig.types'
import { isInternalJigNodeReference } from './jig-internal-nodes'
import { applyViewpointToStage } from './viewpoint/apply-viewpoint-to-stage'
import { buildStageViewSceneContext } from './viewpoint/stage-view-scene-context'

declare var consoleWarn: Function

export function axisAngleToQuaternion(axis: string, angleDegrees: number): { w: number; x: number; y: number; z: number } {
  const angleRadians = angleDegrees * (Math.PI / 180)
  const halfAngle = angleRadians / 2
  const sinHalf = Math.sin(halfAngle)
  const cosHalf = Math.cos(halfAngle)

  let axisX = 0
  let axisY = 0
  let axisZ = 0

  if (axis === 'X') {
    axisX = 1
  } else if (axis === 'Y') {
    axisY = 1
  } else if (axis === 'Z') {
    axisZ = 1
  }

  return {
    w: -cosHalf,
    x: -(axisX * sinHalf),
    y: axisY * sinHalf,
    z: axisZ * sinHalf,
  }
}

export function hexToRGBA(hex: string, alpha: number): string {
  const cleanHex = normalizeHexColor(hex)
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255

  return JSON.stringify({
    r: parseFloat(r.toFixed(6)),
    g: parseFloat(g.toFixed(6)),
    b: parseFloat(b.toFixed(6)),
    a: parseFloat(alpha.toFixed(6)),
  })
}

function normalizeHexColor(hex: string): string {
  const trimmedHex = String(hex || '').trim().replace(/^#/, '')

  if (/^[0-9a-fA-F]{3}$/.test(trimmedHex)) {
    return trimmedHex
      .split('')
      .map(function (char: string) { return char + char })
      .join('')
  }

  if (/^[0-9a-fA-F]{6}$/.test(trimmedHex)) {
    return trimmedHex
  }

  return 'FFFFFF'
}

/**
 * Resolves an AI-provided element name against serialized elements using exact,
 * object-name, uid-fragment, suffix, and partial-match fallbacks.
 */
export function findElementByName(elements: any[], elementName: string): any {
  if (!elements || !elementName) {
    return null
  }

  const searchName = elementName.toLowerCase()
  let element = elements.find(function (el: any) {
    const name = el && el.Name ? String(el.Name).toLowerCase() : ''
    return name === searchName
  })

  if (!element) {
    element = elements.find(function (el: any) {
      const objectName = el && el.ElementData && el.ElementData.ObjectName ? String(el.ElementData.ObjectName).toLowerCase() : ''
      return objectName === searchName
    })
  }

  if (!element && searchName.indexOf('_') >= 0) {
    const parts = searchName.split('_')
    const uidPart = parts.find(function (part: string) {
      return part.indexOf('-') >= 0
    })

    if (uidPart) {
      element = elements.find(function (el: any) {
        const objectName = el && el.ElementData && el.ElementData.ObjectName ? String(el.ElementData.ObjectName).toLowerCase() : ''
        return objectName.indexOf(uidPart) >= 0
      })
    }
  }

  if (!element) {
    const suffix = '_' + searchName
    const endsWithMatches = elements.filter(function (el: any) {
      const objectName = el && el.ElementData && el.ElementData.ObjectName ? String(el.ElementData.ObjectName).toLowerCase() : ''
      return objectName.length > suffix.length && objectName.indexOf(suffix, objectName.length - suffix.length) >= 0
    })
    if (endsWithMatches.length === 1) {
      element = endsWithMatches[0]
    } else if (endsWithMatches.length > 1) {
      consoleWarn('findElementByName: multiple suffix matches for "' + elementName + '", using first of ' + endsWithMatches.length)
      element = endsWithMatches[0]
    }
  }

  if (!element) {
    element = elements.find(function (el: any) {
      const objectName = el && el.ElementData && el.ElementData.ObjectName ? String(el.ElementData.ObjectName).toLowerCase() : ''
      const shortName = el && el.Name ? String(el.Name).toLowerCase() : ''

      return (
        objectName.indexOf(searchName) >= 0 ||
        shortName.indexOf(searchName) >= 0 ||
        searchName.indexOf(objectName) >= 0 ||
        searchName.indexOf(shortName) >= 0
      )
    })
  }

  return element
}

export function getMaterialItemsArray(element: any): any[] | null {
  if (!element || !element.ElementData || !element.ElementData.Material || !element.ElementData.Material.Properties) {
    return null
  }

  if (Array.isArray(element.ElementData.Material.Properties._items)) {
    return element.ElementData.Material.Properties._items
  }

  if (Array.isArray(element.ElementData.Material.Properties._Items)) {
    return element.ElementData.Material.Properties._Items
  }

  return null
}

// ---------------------------------------------------------------------------
// Stage normalization — align tools to a single visual focus element
// ---------------------------------------------------------------------------

function toFocusableElementName(elementName: string | undefined): string | undefined {
  if (!elementName || isInternalJigNodeReference(elementName)) {
    return undefined
  }
  return elementName
}

function cloneToolCollection(collection: ToolData[] | undefined): ToolData[] | undefined {
  if (!Array.isArray(collection)) {
    return undefined
  }
  return collection.map(function (entry) {
    return Object.assign({}, entry)
  })
}

function filterToolsToElementName(collection: ToolData[], elementName: string, limit?: number): ToolData[] {
  var matching = collection.filter(function (entry) {
    return toFocusableElementName(entry.elementName) === elementName
  })
  return typeof limit === 'number' ? matching.slice(0, limit) : matching
}

function resolvePrimaryVisualFocus(stageData: StageInstructionWithTools): string | undefined {
  var fromLabels = Array.isArray(stageData.labels) && stageData.labels.length > 0
    ? toFocusableElementName(stageData.labels[0].elementName)
    : undefined
  if (fromLabels) {
    return fromLabels
  }

  var fromShowcase = Array.isArray(stageData.showcase) && stageData.showcase.length > 0
    ? toFocusableElementName(stageData.showcase[0].elementName)
    : undefined
  if (fromShowcase) {
    return fromShowcase
  }

  var fromHighlights = Array.isArray(stageData.colorHighlights) && stageData.colorHighlights.length > 0
    ? toFocusableElementName(stageData.colorHighlights[0].elementName)
    : undefined
  return fromHighlights
}

function normalizeStageData(stageData: StageInstructionWithTools): StageInstructionWithTools {
  var normalized: StageInstructionWithTools = {
    instruction: stageData.instruction,
    labels: cloneToolCollection(stageData.labels),
    colorHighlights: cloneToolCollection(stageData.colorHighlights),
    showcase: cloneToolCollection(stageData.showcase),
    rotation: cloneToolCollection(stageData.rotation),
    move: cloneToolCollection(stageData.move),
    scale: cloneToolCollection(stageData.scale),
    alpha: cloneToolCollection(stageData.alpha),
    viewpoint: cloneToolCollection(stageData.viewpoint) as any,
  }

  if (!normalized.showcase || !normalized.showcase.length) {
    return normalized
  }

  var focusElementName = resolvePrimaryVisualFocus(stageData)
  if (!focusElementName) {
    normalized.showcase = normalized.showcase.slice(0, 1)
    return normalized
  }

  if (normalized.labels && normalized.labels.length) {
    normalized.labels = filterToolsToElementName(normalized.labels, focusElementName, 1)
  }

  if (normalized.colorHighlights && normalized.colorHighlights.length) {
    normalized.colorHighlights = filterToolsToElementName(normalized.colorHighlights, focusElementName, 1)
  }

  normalized.showcase = [Object.assign({}, normalized.showcase[0], { elementName: focusElementName })]

  if (normalized.viewpoint && normalized.viewpoint.length) {
    normalized.viewpoint = normalized.viewpoint.map(function (entry, index) {
      return index === 0
        ? Object.assign({}, entry, { elementName: focusElementName })
        : Object.assign({}, entry)
    }) as any
  }

  return normalized
}

/**
 * Returns true when the viewpoint explicitly targets an internal wrapper node
 * but the stage has a concrete activated part that should drive framing instead.
 */
function shouldPreferActivatedTarget(explicitElementName: string | undefined, activatedObjectName: string | undefined): boolean {
  if (!explicitElementName || !activatedObjectName || explicitElementName === activatedObjectName) {
    return false
  }
  return isInternalJigNodeReference(explicitElementName)
}

/**
 * Finds the ObjectName of the first element activated (labeled, showcased, or
 * highlighted) in the given stage, so the viewpoint can frame it accurately.
 */
function resolveActivatedObjectName(
  elements: any[],
  stageData: StageInstructionWithTools
): string | undefined {
  var toolCollections = [stageData.labels, stageData.showcase, stageData.colorHighlights]
  for (var i = 0; i < toolCollections.length; i++) {
    var collection = toolCollections[i]
    if (!Array.isArray(collection)) {
      continue
    }
    for (var j = 0; j < collection.length; j++) {
      var tool = collection[j]
      if (!tool || !tool.elementName) {
        continue
      }
      var element = findElementByName(elements, String(tool.elementName))
      if (element && element.ElementData && element.ElementData.ObjectName) {
        return String(element.ElementData.ObjectName)
      }
    }
  }
  return undefined
}

function getStageElementObjectName(element: any): string | undefined {
  return element && element.ElementData && element.ElementData.ObjectName
    ? String(element.ElementData.ObjectName)
    : element && element.ObjectName
      ? String(element.ObjectName)
      : element && element.Name
        ? String(element.Name)
        : undefined
}

function isInternalJigElement(element: any): boolean {
  const objectName = getStageElementObjectName(element)
  return Boolean(objectName && isInternalJigNodeReference(objectName))
}

function getMaterialPropertyValue(element: any, propertyName: string): any | null {
  const materialItems = getMaterialItemsArray(element)
  if (!materialItems) {
    return null
  }

  const property = materialItems.find(function (item: any) {
    return item && item.Name === propertyName
  })

  if (!property || !property.SerializedValue) {
    return null
  }

  try {
    return JSON.parse(property.SerializedValue)
  } catch (_error) {
    return null
  }
}

function getElementAlpha(element: any): number {
  const color = getMaterialPropertyValue(element, '_Color')
  return color && typeof color.a === 'number' ? color.a : 1
}

function isHighlightedElement(element: any): boolean {
  const emission = getMaterialPropertyValue(element, '_EmissionColor')
  if (!emission) {
    return false
  }

  return (
    Math.abs(emission.r || 0) > 1e-6 ||
    Math.abs(emission.g || 0) > 1e-6 ||
    Math.abs(emission.b || 0) > 1e-6
  )
}

function getStageFocusObjectNames(elements: any[]): string[] {
  const focusNames: string[] = []

  function register(element: any): void {
    const objectName = getStageElementObjectName(element)
    if (!objectName || isInternalJigElement(element) || focusNames.indexOf(objectName) >= 0) {
      return
    }

    focusNames.push(objectName)
  }

  elements.forEach(function (element: any) {
    if (element && element.ElementData && element.ElementData.LabelOnForThisStage) {
      register(element)
    }

    if (isHighlightedElement(element)) {
      register(element)
    }
  })

  const visibleElements = elements.filter(function (element: any) {
    const objectName = getStageElementObjectName(element)
    return objectName && !isInternalJigElement(element) && element && element.ElementData && element.ElementData.Hidden !== true
  })
  const hasFadedContext = visibleElements.some(function (element: any) {
    return getElementAlpha(element) < 0.95
  })

  if (hasFadedContext) {
    visibleElements
      .filter(function (element: any) {
        return getElementAlpha(element) >= 0.95
      })
      .forEach(register)
  }

  return focusNames
}

function mergeFocusObjectNames(tool: ViewpointToolData, stageFocusObjectNames: string[]): string[] {
  const names: string[] = []

  function addName(name: string | undefined): void {
    if (name && names.indexOf(name) < 0) {
      names.push(name)
    }
  }

  if (Array.isArray(tool._focusObjectNames)) {
    tool._focusObjectNames.forEach(addName)
  }

  stageFocusObjectNames.forEach(addName)
  return names
}

/**
 * Applies every supported visual tool to a single stage, mutating the serialized
 * stage payload in place so it can be sent back to the Jig API.
 */
export function applyToolsToStage(stage: any, stageData: StageInstructionWithTools, sceneContext?: ViewpointSceneContext | null): void {
  if (!stage || !Array.isArray(stage.SerializedElements)) {
    return
  }

  var normalized = normalizeStageData(stageData)
  const elements = stage.SerializedElements
  const baselineSerializedElements = sceneContext ? JSON.parse(JSON.stringify(stage.SerializedElements)) : null
  const rotatedObjectNames: string[] = []

  if (Array.isArray(normalized.labels)) {
    normalized.labels.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }
      const element = findElementByName(elements, String(tool.elementName))
      if (element && element.ElementData) {
        element.ElementData.Label = String(tool.labelText || '')
        element.ElementData.LabelOnForThisStage = true
      }
    })
  }

  if (Array.isArray(normalized.colorHighlights)) {
    normalized.colorHighlights.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }
      const element = findElementByName(elements, String(tool.elementName))
      if (!element) {
        return
      }

      const objectName = element && element.ElementData && element.ElementData.ObjectName ? String(element.ElementData.ObjectName).toLowerCase() : ''
      if (isInternalJigNodeReference(objectName)) {
        return
      }

      const materialItems = getMaterialItemsArray(element)
      if (!materialItems) {
        return
      }

      const emissionProperty = materialItems.find(function (item: any) {
        return item && item.Name === '_EmissionColor'
      })

      if (emissionProperty) {
        emissionProperty.SerializedValue = hexToRGBA(String(tool.color || '#FFFFFF'), 1.0)
      }
    })
  }

  if (Array.isArray(normalized.showcase)) {
    normalized.showcase.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }

      const target = findElementByName(elements, String(tool.elementName))
      if (!target) {
        return
      }

      const fadeAlpha = typeof tool.fadeAlpha === 'number' ? tool.fadeAlpha : 0.2

      elements.forEach(function (element: any) {
        const objectName = element && element.ElementData && element.ElementData.ObjectName ? String(element.ElementData.ObjectName).toLowerCase() : ''
        if (isInternalJigNodeReference(objectName)) {
          return
        }

        const materialItems = getMaterialItemsArray(element)
        if (!materialItems) {
          return
        }

        const colorProperty = materialItems.find(function (item: any) {
          return item && item.Name === '_Color'
        })

        if (!colorProperty || !colorProperty.SerializedValue) {
          return
        }

        try {
          const colorValue = JSON.parse(colorProperty.SerializedValue)
          colorValue.a = parseFloat((element === target ? 1 : fadeAlpha).toFixed(6))
          colorProperty.SerializedValue = JSON.stringify(colorValue)
        } catch (_error) {
          // keep existing value on invalid JSON
        }
      })
    })
  }

  if (Array.isArray(normalized.rotation)) {
    normalized.rotation.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }
      const element = findElementByName(elements, String(tool.elementName))
      if (!element || !element.ElementData) {
        return
      }

      const axis = String(tool.axis || 'Y')
      const angle = typeof tool.angle === 'number' ? tool.angle : 0
      const quat = axisAngleToQuaternion(axis, angle)

      if (!element.ElementData.Rotation) {
        element.ElementData.Rotation = { x: 0, y: 0, z: 0, w: 1 }
      }

      element.ElementData.Rotation.w = quat.w
      element.ElementData.Rotation.x = quat.x
      element.ElementData.Rotation.y = quat.y
      element.ElementData.Rotation.z = quat.z

      const objectName = element.ElementData.ObjectName ? String(element.ElementData.ObjectName) : ''
      if (objectName && rotatedObjectNames.indexOf(objectName) < 0) {
        rotatedObjectNames.push(objectName)
      }
    })
  }

  if (Array.isArray(normalized.move)) {
    normalized.move.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }
      const element = findElementByName(elements, String(tool.elementName))
      if (!element || !element.ElementData) {
        return
      }

      if (!element.ElementData.Position) {
        element.ElementData.Position = { x: 0, y: 0, z: 0 }
      }

      const dx = typeof tool.x === 'number' ? tool.x : 0
      const dy = typeof tool.y === 'number' ? tool.y : 0
      const dz = typeof tool.z === 'number' ? tool.z : 0
      element.ElementData.Position.x += dx
      element.ElementData.Position.y += dy
      element.ElementData.Position.z += dz
    })
  }

  if (Array.isArray(normalized.scale)) {
    normalized.scale.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }
      const element = findElementByName(elements, String(tool.elementName))
      if (!element || !element.ElementData) {
        return
      }

      if (!element.ElementData.Scale) {
        element.ElementData.Scale = { x: 1, y: 1, z: 1 }
      }

      if (typeof tool.uniform === 'number') {
        element.ElementData.Scale.x = tool.uniform
        element.ElementData.Scale.y = tool.uniform
        element.ElementData.Scale.z = tool.uniform
        return
      }

      if (typeof tool.x === 'number') {
        element.ElementData.Scale.x = tool.x
      }
      if (typeof tool.y === 'number') {
        element.ElementData.Scale.y = tool.y
      }
      if (typeof tool.z === 'number') {
        element.ElementData.Scale.z = tool.z
      }
    })
  }

  if (Array.isArray(normalized.alpha)) {
    normalized.alpha.forEach(function (tool: ToolData) {
      if (!tool || !tool.elementName) {
        return
      }
      const element = findElementByName(elements, String(tool.elementName))
      if (!element || !element.ElementData) {
        return
      }
      element.ElementData.Alpha = typeof tool.alpha === 'number' ? tool.alpha : 1
    })
  }

  // Apply viewpoint LAST — after element tools have run.
  // If the viewpoint explicitly targets an internal wrapper node but a concrete
  // part was activated in this stage, prefer the activated part for framing.
  if (Array.isArray(normalized.viewpoint) && normalized.viewpoint.length > 0) {
    var vpTools = normalized.viewpoint.slice() as ViewpointToolData[]
    var activatedObjectName = resolveActivatedObjectName(elements, normalized)
    var firstVp = vpTools[0]
    var stageFocusObjectNames = getStageFocusObjectNames(elements)
    var stageSceneContext = sceneContext
      ? buildStageViewSceneContext({
        baseSceneContext: sceneContext,
        baselineSerializedElements: baselineSerializedElements || undefined,
        currentSerializedElements: elements,
        rotatedObjectNames: rotatedObjectNames,
      }) || sceneContext
      : null
    if (firstVp) {
      var explicitTarget = firstVp.elementName
      var mergedFocusObjectNames = mergeFocusObjectNames(firstVp, stageFocusObjectNames)
      var firstVpUpdates: Partial<ViewpointToolData> = {
        _focusObjectNames: mergedFocusObjectNames,
      }

      if (shouldPreferActivatedTarget(explicitTarget, activatedObjectName)) {
        firstVpUpdates.elementName = activatedObjectName
      } else if (!explicitTarget && activatedObjectName) {
        firstVpUpdates.elementName = activatedObjectName
      }

      vpTools[0] = Object.assign({}, firstVp, firstVpUpdates)
    }
    applyViewpointToStage(stage, vpTools, stageSceneContext || null)
  }
}
